import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { environment } from 'src/environments/environment';
import { MetricasService } from '../../service/metricas.service';
import { FiltroPeriodoComponent } from '../filtros-metricas/filtro-periodo/filtro-periodo.component';
import { FiltroResponsavelComponent } from '../filtros-metricas/filtro-responsavel/filtro-responsavel.component';

interface Comparativo {
  selected: boolean;
  responsibleId: number;
  responsibleName: string;
  lastActivity: string;
  access: number;
  search: number;
  prospecting: number;
  contracted: number;
  lost: number;
  actions: number;
}

@Component({
  selector: 'app-mapa-comparativo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FiltroPeriodoComponent,
    FiltroResponsavelComponent,
  ],
  templateUrl: './mapa-comparativo.component.html',
  styleUrls: ['./mapa-comparativo.component.scss'],
})
export class MapaComparativoComponent {
  @Input() responsavel: any[] = [];
  BASE_IMG_URL: string = environment.BASE_IMG_URL;
  ordemUltimaAtividadeAsc: boolean = true;

  config: Partial<BsDaterangepickerConfig> = {
    containerClass: 'bs-datepicker',
    rangeInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    selectFromOtherMonth: true,
    isAnimated: true,
    displayMonths: 1,
  };

  @Output() periodoChange = new EventEmitter<{
    periodBegin: string;
    periodEnd: string;
  } | null>();
  @Output() atualizandoMetricasChange = new EventEmitter<boolean>();
  @Input() selectedPeriodo: Date[] | null = null;
  @Input() selectedUsuario: string = 'Todos';

  get desabilitarExportar(): boolean {
    return this.comparativos.length === 0;
  }

  intervaloSelecionado: Date[] | null = null;

  private colunaOrdenada: keyof Comparativo | null = null;
  private ordemAsc: boolean = true;

  comparativoCampos: (keyof Comparativo)[] = [
    'access',
    'search',
    'prospecting',
    'contracted',
    'lost',
    'actions',
  ];

  comparativos: Comparativo[] = [];

  constructor(private _metricasService: MetricasService) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPeriodo'] && !changes['selectedPeriodo'].firstChange) {
      const periodo =
        this.selectedPeriodo?.length === 2
          ? {
              periodBegin: this.selectedPeriodo[0].toISOString().slice(0, 10),
              periodEnd: this.selectedPeriodo[1].toISOString().slice(0, 10),
            }
          : undefined;
      this.fetchComparativeMap(periodo);
    }
  }

  exportExcel() {
    this.atualizandoMetricasChange.emit(true);
    const filtros = this.capturarFiltrosAtuais();
    this._metricasService.downloadExcelComparativeMap(filtros).subscribe(
      (response) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'mapa-comparativo.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.atualizandoMetricasChange.emit(false);
      },
      (error) => {
        this.atualizandoMetricasChange.emit(false);
        console.error('Erro ao exportar Excel:', error);
      }
    );
  }

  capturarFiltrosAtuais(): any {
    let period: { periodBegin: string; periodEnd: string } | null = null;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      period = {
        periodBegin: this.formatDateToBackendFormat(this.selectedPeriodo[0]),
        periodEnd: this.formatDateToBackendFormat(this.selectedPeriodo[1]),
      };
    }

    let responsibleId: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleId = Number(this.selectedUsuario) || 0;
    }

    const filtros: any = {};

    if (period) {
      filtros.period = period;
    }

    if (responsibleId !== 0) {
      filtros.responsible = {
        responsible: responsibleId
      };
    }

    return filtros;
  }

  get selecionados(): Comparativo[] {
    return this.comparativos.filter((c) => c.selected);
  }

  todosSelecionados(): boolean {
    return (
      this.comparativos.length > 0 && this.comparativos.every((c) => c.selected)
    );
  }

  toggleSelecionarTodos(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.comparativos.forEach((c) => (c.selected = checked));
  }

  onResponsavelIdChange(responsavelId: number): void {
    const periodo = this.periodObjFromSelected();
    this.fetchComparativeMap(periodo);
  }

  private periodObjFromSelected():
    | { periodBegin: string; periodEnd: string }
    | undefined {
    if (this.selectedPeriodo?.length === 2) {
      return {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    return undefined;
  }

  private formatYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDateToBackendFormat(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  fetchComparativeMap(periodo?: {
    periodBegin: string;
    periodEnd: string;
  }): void {
    const body = {
      responsibleID:
        this.selectedUsuario === 'Todos'
          ? 0
          : Number(this.selectedUsuario) || 0,
      periodBegin: periodo?.periodBegin ?? '',
      periodEnd: periodo?.periodEnd ?? '',
    };

    this.atualizandoMetricasChange.emit(true);
    this._metricasService.listComparativeMap(body).subscribe({
      next: (res) => {
        this.comparativos = (res?.data || []).map((item: any) => ({
          selected: false,
          responsibleId: item.responsibleId || 0,
          responsibleName: item.responsibleName || '',
          lastActivity: this.formatDate(item.lastActivity),
          access: item.access || 0,
          search: item.search || 0,
          prospecting: item.prospecting || 0,
          contracted: item.contracted || 0,
          lost: item.lost || 0,
          actions: item.actions || 0,
        }));
        this.atualizandoMetricasChange.emit(false);
      },
      error: (err) => {
        console.error('Erro ao buscar mapa comparativo:', err);
        this.comparativos = [];
        this.atualizandoMetricasChange.emit(false);
      },
    });
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private parseYmdToLocal(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  onPeriodoSelecionadoChange(
    periodo: { periodBegin: string; periodEnd: string } | null
  ) {
    if (periodo && periodo.periodBegin && periodo.periodEnd) {
      this.selectedPeriodo = [
        this.parseYmdToLocal(periodo.periodBegin),
        this.parseYmdToLocal(periodo.periodEnd),
      ];
    } else {
      this.selectedPeriodo = null;
    }
    this.fetchComparativeMap(periodo ?? undefined);
  }

  getMaior(campo: keyof Comparativo): number | null {
    const selecionados = this.selecionados;
    if (selecionados.length < 2) return null;
    return Math.max(...selecionados.map((c) => this.parseValor(c[campo])));
  }

  getMenor(campo: keyof Comparativo): number | null {
    const selecionados = this.selecionados;
    if (selecionados.length < 2) return null;
    return Math.min(...selecionados.map((c) => this.parseValor(c[campo])));
  }

  private parseValor(valor: any): number {
    if (typeof valor === 'string' && valor.includes('%')) {
      return parseFloat(valor.replace('%', '')) || 0;
    }
    return Number(valor) || 0;
  }

  getComparativoClass(c: Comparativo, campo: keyof Comparativo): string {
    if (!c.selected) return 'comparativo-nao-selecionado';
    const selecionados = this.selecionados;
    if (selecionados.length < 2) return '';

    const valores = selecionados.map((r) => this.parseValor(r[campo]));
    const valor = this.parseValor(c[campo]);
    const maior = Math.max(...valores);
    const menor = Math.min(...valores);
    const invertido = campo === 'lost';

    if (maior === menor) {
      return (
        (invertido ? 'comparativo-menor' : 'comparativo-maior') +
        ' comparativo-empate'
      );
    }

    if (invertido) {
      if (valor === menor) {
        const minTie = valores.filter((v) => v === menor).length > 1;
        return 'comparativo-maior' + (minTie ? ' comparativo-empate' : '');
      } else {
        const maxTie =
          valores.filter((v) => v === maior).length > 1 && valor === maior;
        return 'comparativo-menor' + (maxTie ? ' comparativo-empate' : '');
      }
    } else {
      if (valor === maior) {
        const maxTie = valores.filter((v) => v === maior).length > 1;
        return 'comparativo-maior' + (maxTie ? ' comparativo-empate' : '');
      } else {
        const minTie =
          valores.filter((v) => v === menor).length > 1 && valor === menor;
        return 'comparativo-menor' + (minTie ? ' comparativo-empate' : '');
      }
    }
  }

  ordenarUltimaAtividade() {
    this.ordemUltimaAtividadeAsc = !this.ordemUltimaAtividadeAsc;
    this.comparativos.sort((a, b) => {
      const dataA = this.parseData(a.lastActivity);
      const dataB = this.parseData(b.lastActivity);
      if (this.ordemUltimaAtividadeAsc) {
        return dataA.getTime() - dataB.getTime();
      } else {
        return dataB.getTime() - dataA.getTime();
      }
    });
  }

  private parseData(dataStr: string): Date {
    const [dia, mes, ano] = dataStr.split('/').map(Number);
    const fullAno = ano < 100 ? 2000 + ano : ano;
    return new Date(fullAno, mes - 1, dia);
  }

  ordenarColuna(coluna: keyof Comparativo) {
    if (this.colunaOrdenada === coluna) {
      this.ordemAsc = !this.ordemAsc;
    } else {
      this.colunaOrdenada = coluna;
      this.ordemAsc = true;
    }
    this.comparativos.sort((a, b) => {
      const valorA = this.parseValor(a[coluna]);
      const valorB = this.parseValor(b[coluna]);
      return this.ordemAsc ? valorA - valorB : valorB - valorA;
    });
  }
}
